import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
  CircularProgress,
  Container,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import Groups2Icon from "@mui/icons-material/Groups2";

import UsersTable from "../components/User/UsersTable";
import PasswordModal from "../components/User/PasswordModal";
import UserPermissionsDialog from "../components/User/UserPermissionsDialog";
import AppDialog from "../components/General/Dialog/AppDialog";
import ConfirmDialog from "../components/General/Dialog/ConfirmDialog";
import ActionHeaderCard from "../components/General/ActionHeaderCard";

import {
  getUsers,
  createUser,
  deleteUser,
  updateUser,
  getRoles,
  getUserDirectPermissions,
  updateUserDirectPermissions,
} from "../services/UserService";
import { getBranches } from "../services/BranchService";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

export default function User() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission("crear usuario");
  const canView = hasPermission("ver usuario");

  const emptyForm = useMemo(
    () => ({
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
      role: "",
      branches: [],
    }),
    [],
  );

  const [form, setForm] = useState(emptyForm);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formOpen, setFormOpen] = useState(false);

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState(null);

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [directPermissions, setDirectPermissions] = useState([]);
  const [permissionsViaRole, setPermissionsViaRole] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await getUsers();
      setUsers(response?.data?.data || []);
    } catch {
      setUsers([]);
      throw new Error("Error al cargar usuarios");
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await getRoles();
      setRoles(response?.data?.data || []);
    } catch {
      setRoles([]);
      throw new Error("Error al cargar roles");
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await getBranches({ is_active: true });
      setBranches(response?.data?.data || []);
    } catch {
      setBranches([]);
      throw new Error("Error al cargar sucursales");
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    setTableLoading(true);
    try {
      await Promise.all([fetchUsers(), fetchRoles(), fetchBranches()]);
    } catch {
      showToast("Error al cargar la información inicial.", "error")
    } finally {
      setTableLoading(false);
    }
  }, [fetchUsers, fetchRoles, fetchBranches, showToast]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditMode(false);
    setEditId(null);
    setErrors({});    setShowPassword(false);
    setShowPasswordConfirm(false);
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEdit = (user) => {
    setForm({
      name: user?.name || "",
      email: user?.email || "",
      password: "",
      password_confirmation: "",
      role: user?.role || "",
      branches: Array.isArray(user?.branches)
        ? user.branches.map((branch) => branch.id)
        : [],
    });

    setEditId(user?.id || null);
    setEditMode(true);
    setErrors({});    setShowPassword(false);
    setShowPasswordConfirm(false);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    resetForm();
  };

  const validate = () => {
    const temp = {};
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

    temp.name = form.name?.trim() ? "" : "Nombre requerido";
    temp.email = form.email?.trim() ? "" : "Correo requerido";
    temp.role = form.role ? "" : "Rol requerido";
    temp.branches =
      Array.isArray(form.branches) && form.branches.length > 0
        ? ""
        : "Debe seleccionar al menos una sucursal";

    if (!editMode) {
      if (!form.password) {
        temp.password = "Contraseña requerida";
      } else if (!passwordRegex.test(form.password)) {
        temp.password =
          "Debe tener mínimo 8 caracteres, una mayúscula, una minúscula y un número";
      }

      if (!form.password_confirmation) {
        temp.password_confirmation = "Debe confirmar la contraseña";
      } else if (form.password !== form.password_confirmation) {
        temp.password_confirmation = "Las contraseñas no coinciden";
      }
    }

    setErrors(temp);
    return Object.values(temp).every((value) => value === "");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const payload = editMode
        ? {
            name: form.name,
            email: form.email,
            role: form.role,
            branches: form.branches,
          }
        : {
            name: form.name,
            email: form.email,
            password: form.password,
            password_confirmation: form.password_confirmation,
            role: form.role,
            branches: form.branches,
          };

      const response = editMode
        ? await updateUser(editId, payload)
        : await createUser(payload);

      if (response?.data?.status === "success") {
        showToast(
          response?.data?.message ||
            (editMode
              ? "Usuario actualizado correctamente."
              : "Usuario creado correctamente."),
        "success")
      }

      await fetchUsers();
      closeForm();
    } catch (error) {
      if (error?.response?.data?.errors) {
        const apiErrors = {};

        Object.keys(error.response.data.errors).forEach((key) => {
          apiErrors[key] = error.response.data.errors[key][0];
        });

        setErrors(apiErrors);
      } else if (error?.response?.data?.message) {
        showToast(error.response.data.message, "error")
      } else {
        showToast("Ocurrió un error inesperado.", "error")
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (id) => {
    setSelectedDeleteId(id);
    setOpenDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    setOpenDeleteConfirm(false);
    setSelectedDeleteId(null);
  };

  const handleDelete = async () => {
    if (!selectedDeleteId) return;

    try {
      const response = await deleteUser(selectedDeleteId);

      if (response?.data?.status === "success") {
        showToast(
          response?.data?.message || "Usuario eliminado correctamente.",
        "success")
        await fetchUsers();
        handleCloseDeleteConfirm();
      } else {
        showToast(response?.data?.message || "Error al eliminar usuario.", "error")
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        showToast(error.response.data.message, "error")
      } else {
        showToast("Error al eliminar usuario.", "error")
      }
    }
  };

  const handleUpdatePassword = (userId) => {
    setPasswordUserId(userId);
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordUserId(null);
  };

  const handlePasswordSuccess = async (message) => {
    showToast(message || "Contraseña actualizada correctamente.", "success")
    handleClosePasswordModal();
    await fetchUsers();
  };

  const handleOpenPermissions = async (user) => {
    setPermissionsDialogOpen(true);
    setPermissionsLoading(true);
    setSelectedUser(user);
    setDirectPermissions([]);
    setPermissionsViaRole([]);
    setAvailablePermissions([]);
    try {
      const response = await getUserDirectPermissions(user.id);
      const data = response?.data?.data || {};

      setDirectPermissions(data.direct_permissions || []);
      setPermissionsViaRole(data.permissions_via_role || []);
      setAvailablePermissions(data.available_permissions || []);
    } catch (error) {
      setPermissionsDialogOpen(false);
      setSelectedUser(null);
      showToast(
        error?.response?.data?.message ||
          "No se pudieron cargar los permisos del usuario.",
      "error")
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleClosePermissionsDialog = () => {
    if (permissionsSaving) return;

    setPermissionsDialogOpen(false);
    setSelectedUser(null);
    setDirectPermissions([]);
    setPermissionsViaRole([]);
    setAvailablePermissions([]);
  };

  const handleRemoveDirectPermission = (permission) => {
    setDirectPermissions((prev) =>
      prev.filter((item) => item.name !== permission.name),
    );

    setAvailablePermissions((prev) => {
      const exists = prev.some((item) => item.name === permission.name);
      if (exists) return prev;

      return [...prev, permission].sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  const handleAddDirectPermission = (permission) => {
    const alreadyExists = directPermissions.some(
      (item) => item.name === permission.name,
    );

    if (alreadyExists) return;

    setDirectPermissions((prev) =>
      [...prev, permission].sort((a, b) => a.name.localeCompare(b.name)),
    );

    setAvailablePermissions((prev) =>
      prev.filter((item) => item.name !== permission.name),
    );
  };

  const handleSaveDirectPermissions = async () => {
    if (!selectedUser) return;

    setPermissionsSaving(true);
    try {
      const response = await updateUserDirectPermissions(selectedUser.id, {
        permissions: directPermissions.map((permission) => permission.name),
      });

      if (response?.data?.status === "success") {
        showToast(
          response?.data?.message ||
            "Permisos directos actualizados correctamente.",
        "success")
      }

      await fetchUsers();
      handleClosePermissionsDialog();
    } catch (error) {
      showToast(
        error?.response?.data?.message ||
          "No se pudieron actualizar los permisos directos.",
      "error")
    } finally {
      setPermissionsSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={2}>

        <Grid size={{ xs: 12 }}>
          <Grid size={{ xs: 12 }}>
            <ActionHeaderCard
              icon={<Groups2Icon />}
              title="Usuarios"
              description="Administrar usuarios registrados."
              action={
                canCreate ? (
                  <Button
                    onClick={openCreate}
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 800,
                      textTransform: "none",
                    }}
                  >
                    Nuevo usuario
                  </Button>
                ) : null
              }
            />
          </Grid>
        </Grid>

        {canView && (
          <Grid size={{ xs: 12 }}>
            <UsersTable
              users={users}
              loading={tableLoading}
              onDelete={handleOpenDeleteConfirm}
              onEdit={openEdit}
              onUpdatePassword={handleUpdatePassword}
              onManagePermissions={handleOpenPermissions}
            />
          </Grid>
        )}

        <PasswordModal
          open={showPasswordModal}
          onClose={handleClosePasswordModal}
          userId={passwordUserId}
          onSuccess={handlePasswordSuccess}
        />
      </Grid>

      <AppDialog
        open={formOpen}
        onClose={closeForm}
        maxWidth="sm"
        title={editMode ? "Editar usuario" : "Nuevo usuario"}
        subtitle={
          editMode
            ? "Actualizá los datos del usuario."
            : "Completá el formulario para registrar un usuario."
        }
        icon={<Groups2Icon />}
        actions={null}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Nombre"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Correo"
                name="email"
                value={form.email}
                onChange={handleChange}
                type="email"
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required error={!!errors.role}>
                <InputLabel id="role-label">Rol</InputLabel>
                <Select
                  labelId="role-label"
                  name="role"
                  label="Rol"
                  value={form.role}
                  onChange={handleChange}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.name}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.role}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                options={branches}
                getOptionLabel={(option) => option.name || ""}
                value={branches.filter((branch) =>
                  Array.isArray(form.branches)
                    ? form.branches.includes(branch.id)
                    : false,
                )}
                onChange={(_, newValue) => {
                  setForm((prev) => ({
                    ...prev,
                    branches: newValue.map((branch) => branch.id),
                  }));

                  setErrors((prev) => ({
                    ...prev,
                    branches: "",
                  }));
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sucursales"
                    placeholder="Seleccionar sucursales"
                    error={!!errors.branches}
                    helperText={errors.branches}
                  />
                )}
              />
            </Grid>

            {!editMode && (
              <>
                <Grid size={{ xs: 12 }}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!!errors.password}
                  >
                    <InputLabel>Contraseña</InputLabel>
                    <OutlinedInput
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      label="Contraseña"
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    <FormHelperText>{errors.password}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!!errors.password_confirmation}
                  >
                    <InputLabel>Confirmar contraseña</InputLabel>
                    <OutlinedInput
                      type={showPasswordConfirm ? "text" : "password"}
                      name="password_confirmation"
                      value={form.password_confirmation}
                      onChange={handleChange}
                      label="Confirmar contraseña"
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowPasswordConfirm((prev) => !prev)
                            }
                            edge="end"
                          >
                            {showPasswordConfirm ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    <FormHelperText>
                      {errors.password_confirmation}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12 }}>
              <Button
                variant="contained"
                type="submit"
                fullWidth
                sx={{ py: 1.4, borderRadius: 2, fontWeight: 800 }}
                endIcon={!loading ? <SendIcon /> : null}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : editMode ? (
                  "Actualizar"
                ) : (
                  "Registrar"
                )}
              </Button>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ py: 1.2, borderRadius: 2 }}
                onClick={closeForm}
                endIcon={<CancelIcon />}
                disabled={loading}
              >
                Cancelar
              </Button>
            </Grid>
          </Grid>
        </Box>
      </AppDialog>

      <UserPermissionsDialog
        open={permissionsDialogOpen}
        onClose={handleClosePermissionsDialog}
        onSave={handleSaveDirectPermissions}
        loading={permissionsLoading}
        saving={permissionsSaving}
        user={selectedUser}
        directPermissions={directPermissions}
        permissionsViaRole={permissionsViaRole}
        availablePermissions={availablePermissions}
        onRemoveDirectPermission={handleRemoveDirectPermission}
        onAddDirectPermission={handleAddDirectPermission}
      />

      <ConfirmDialog
        open={openDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleDelete}
        title="Confirmar eliminación"
        description="¿Eliminar este usuario? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmColor="error"
      />
    </Container>
  );
}
